import { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { SingleCustomFieldFilter } from '~/components/Filters/CustomFieldFilter/SingleCustomFieldFilter';
import { getAppliedParamsCount } from '~/components/Filters/FiltersBar/filtersBarUtils';
import { FilterTrigger } from '~/components/Filters/SingleFilters/FilterTrigger';
import { type SingleFilterProps } from '~/components/Filters/types';
import { baseFiltersOptions, type CustomFieldFilterType } from '~/components/FiltersMenu/types';
import { useFilterParams } from '~/hooks/filters/useFilterParams';
import { removeFilterAction } from '~/store/filters/actions';
import { type SelectedFilter } from '~/store/filters/types';

export type CustomFieldFilterTriggerProps = {
  customField: CustomFieldFilterType;
} & SingleFilterProps;

export const CustomFieldFilterTrigger = memo(
  ({ onRemoveClick, customField, onApplyClick, onOpenChange }: CustomFieldFilterTriggerProps) => {
    const dispatch = useDispatch();
    const { customFieldsParam } = useFilterParams();

    const content = useMemo(
      () => <SingleCustomFieldFilter customField={customField} onApplyClick={onApplyClick} />,
      [customField, onApplyClick],
    );

    const appliedCustomFieldsCount = useMemo(() => {
      const fieldParam = customFieldsParam.find((field) => field.id === customField.id);
      return getAppliedParamsCount(fieldParam?.filter);
    }, [customField.id, customFieldsParam]);

    const removeFilter = useCallback(async () => {
      const filter: SelectedFilter = { type: 'customField', customField };
      dispatch(removeFilterAction({ filter }));
      onRemoveClick?.([filter]);
    }, [customField, dispatch, onRemoveClick]);

    return (
      <FilterTrigger
        onRemoveClick={removeFilter}
        count={appliedCustomFieldsCount}
        filterContent={content}
        title={customField.name}
        Icon={baseFiltersOptions['customField'].icon}
        onOpenChange={onOpenChange}
      />
    );
  },
);

CustomFieldFilterTrigger.displayName = 'CustomFieldFilterTrigger';
